import { X, Settings, ChevronDown } from 'lucide-react';
import { useState, useEffect, useId } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useClerkApi } from '../hooks/useClerkApi';
import { SeasonList } from './SeasonList';
import { seasonQuery } from '../utils/season';
import { searchWith } from '../utils/leagueRoutes';
import { useDialogFocus } from '../hooks/useDialogFocus';

const rowStyle = (isActive) => ({
  display: 'flex', alignItems: 'center', gap: '10px',
  padding: '12px', borderRadius: '6px', width: '100%', cursor: 'pointer',
  background: 'var(--ledger-panel)',
  border: `1px solid ${isActive ? 'var(--ledger-ink)' : 'var(--ledger-line)'}`,
  color: 'var(--ledger-ink)',
});

export function LeagueSwitcherSheet({ isOpen, onClose, leagues, activeLeagueId, activeSeason, onSelectLeague }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { authFetch } = useClerkApi();
  const [seasonsExpanded, setSeasonsExpanded] = useState(false);
  useEffect(() => { setSeasonsExpanded(false); }, [activeLeagueId]);
  const titleId = useId();
  const dialogRef = useDialogFocus({ open: isOpen, onClose });
  if (!isOpen) return null;

  return (
    <>
      {/* Scrim */}
      <div
        aria-hidden="true"
        onClick={onClose}
        style={{ position: 'fixed', inset: 0, zIndex: 40, background: 'rgba(0, 0, 0, 0.5)' }}
      />
      {/* Sheet */}
      <div ref={dialogRef} role="dialog" aria-modal="true" aria-labelledby={titleId} tabIndex={-1} className="ledger-scope" style={{
        position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 50,
        background: 'var(--ledger-panel)',
        color: 'var(--ledger-ink)',
        borderTop: '1px solid var(--ledger-line)',
        borderRadius: '16px 16px 0 0',
        padding: '12px 14px calc(env(safe-area-inset-bottom, 0px) + 24px)',
        boxShadow: '0 -8px 24px rgba(0, 0, 0, 0.12)',
        maxHeight: '80vh', overflowY: 'auto',
      }}>
        <div aria-hidden="true" style={{ width: '40px', height: '4px', borderRadius: '2px', background: 'var(--ledger-line-2)', margin: '0 auto 12px' }} />

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
          <h2 id={titleId} style={{ margin: 0, fontSize: '15px', fontWeight: 600 }}>Switch league</h2>
          <button type="button" aria-label="Close" onClick={onClose} className="ledger-icon-btn">
            <X aria-hidden="true" style={{ width: '15px', height: '15px' }} />
          </button>
        </div>

        {/* League list */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '12px' }}>
          {leagues.map(league => {
            const isActive = league.league_id === activeLeagueId;
            return (
              <div key={league.league_id}>
                <button
                  type="button"
                  onClick={() => {
                    if (isActive) {
                      setSeasonsExpanded(e => !e);
                      return;
                    }
                    onSelectLeague(league.league_id);
                    onClose();
                  }}
                  style={rowStyle(isActive)}
                >
                  <span aria-hidden="true" style={{ width: '3px', height: '26px', borderRadius: '2px', flexShrink: 0, background: isActive ? 'var(--you)' : 'var(--ledger-line-2)' }} />
                  <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '2px', flex: 1, minWidth: 0 }}>
                    <span style={{ fontSize: '14px', fontWeight: 600, textAlign: 'left' }}>
                      {league.name}
                    </span>
                    {league.provider && (
                      <span style={{ fontSize: '12px', color: 'var(--ledger-muted)' }}>
                        {league.provider === 'ESPN' ? 'ESPN · NFL' : 'Yahoo · NHL'}
                      </span>
                    )}
                  </span>
                  {isActive && (
                    <ChevronDown aria-hidden="true" style={{
                      width: '14px', height: '14px', color: 'var(--ledger-muted)', flexShrink: 0,
                      transform: seasonsExpanded ? 'rotate(180deg)' : 'none', transition: 'transform 150ms ease',
                    }} />
                  )}
                </button>
                {isActive && seasonsExpanded && (
                  <div style={{ padding: '8px 4px 4px 16px' }}>
                    <SeasonList
                        leagueId={league.league_id}
                        authFetch={authFetch}
                        activeSeason={activeSeason}
                        onSelectSeason={(entry) => {
                          const isLive = entry.status === 'live';
                          if (!isLive && seasonQuery(entry.season) == null) return;
                          navigate({ pathname: location.pathname, search: searchWith(location.search, { season: isLive ? null : entry.season, changes: null }) });
                          onClose();
                        }}
                      />
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div style={{ height: '1px', background: 'var(--ledger-line)', marginBottom: '12px' }} />

        {/* Settings */}
        <button
          type="button"
          onClick={() => { navigate('/settings', { state: { from: window.location.pathname + window.location.search } }); onClose(); }}
          style={rowStyle(false)}
        >
          <Settings aria-hidden="true" style={{ width: '15px', height: '15px', color: 'var(--ledger-muted)', flexShrink: 0 }} />
          <span style={{ fontSize: '14px' }}>Account settings</span>
        </button>
      </div>
    </>
  );
}

export default LeagueSwitcherSheet;
